import { schemaMigrations, addColumns, createTable, unsafeExecuteSql } from '@nozbe/watermelondb/Schema/migrations'

export default schemaMigrations({
    migrations: [
        {
            // v1 → v2: Identity migration — v1 and v2 schemas are structurally
            // identical. This entry exists because WatermelonDB requires a
            // contiguous migration chain from migrationsEnabledAtVersion (1)
            // to the current schema version. Do NOT remove this entry;
            // doing so breaks migration resolution for devices that were
            // initialized at v1.
            toVersion: 2,
            steps: [],
        },
        {
            // v2 → v3: Add purchasing returns, supplier payments, and GST fields
            toVersion: 3,
            steps: [
                // Amend purchase_orders: add supplier invoice reference fields
                addColumns({
                    table: 'purchase_orders',
                    columns: [
                        { name: 'supplier_invoice_no', type: 'string', isOptional: true },
                        { name: 'supplier_invoice_date', type: 'number', isOptional: true },
                    ],
                }),

                // Amend po_line_items: add GST/ITC fields for GSTR-2A reconciliation
                addColumns({
                    table: 'po_line_items',
                    columns: [
                        { name: 'tax_rate', type: 'number' },
                        { name: 'cgst_amount', type: 'number' },
                        { name: 'sgst_amount', type: 'number' },
                        { name: 'igst_amount', type: 'number' },
                    ],
                }),

                // Fix missing columns from previous architectural updates
                addColumns({
                    table: 'settings',
                    columns: [
                        { name: 'store_id', type: 'string', isOptional: true, isIndexed: true },
                    ],
                }),

                addColumns({
                    table: 'audit_logs',
                    columns: [
                        { name: 'merchant_id', type: 'string', isOptional: true, isIndexed: true },
                    ],
                }),

                addColumns({
                    table: 'sync_logs',
                    columns: [
                        { name: 'merchant_id', type: 'string', isOptional: true, isIndexed: true },
                    ],
                }),

                // Create supplier_payments table
                createTable({
                    name: 'supplier_payments',
                    columns: [
                        { name: 'store_id', type: 'string', isIndexed: true },
                        { name: 'supplier_id', type: 'string', isIndexed: true },
                        { name: 'amount', type: 'number' },
                        { name: 'method', type: 'string' },
                        { name: 'transaction_ref', type: 'string', isOptional: true },
                        { name: 'payment_date', type: 'number', isIndexed: true },
                        { name: 'notes', type: 'string', isOptional: true },
                        { name: 'version', type: 'number' },
                        { name: 'is_synced', type: 'boolean' },
                        { name: 'is_deleted', type: 'boolean' },
                        { name: 'created_at', type: 'number' },
                        { name: 'updated_at', type: 'number' },
                    ],
                }),

                // Create purchase_returns table
                createTable({
                    name: 'purchase_returns',
                    columns: [
                        { name: 'store_id', type: 'string', isIndexed: true },
                        { name: 'supplier_id', type: 'string', isIndexed: true },
                        { name: 'purchase_order_id', type: 'string', isOptional: true },
                        { name: 'return_number', type: 'string' },
                        { name: 'status', type: 'string' },
                        { name: 'reason', type: 'string', isOptional: true },
                        { name: 'total_amount', type: 'number' },
                        { name: 'notes', type: 'string', isOptional: true },
                        { name: 'version', type: 'number' },
                        { name: 'is_synced', type: 'boolean' },
                        { name: 'is_deleted', type: 'boolean' },
                        { name: 'created_at', type: 'number' },
                        { name: 'updated_at', type: 'number' },
                    ],
                }),

                // Create purchase_return_items table
                createTable({
                    name: 'purchase_return_items',
                    columns: [
                        { name: 'purchase_return_id', type: 'string', isIndexed: true },
                        { name: 'product_id', type: 'string' },
                        { name: 'batch_id', type: 'string', isOptional: true },
                        { name: 'quantity', type: 'number' },
                        { name: 'unit_price', type: 'number' },
                        { name: 'reason', type: 'string', isOptional: true },
                        { name: 'version', type: 'number' },
                        { name: 'is_synced', type: 'boolean' },
                        { name: 'is_deleted', type: 'boolean' },
                        { name: 'created_at', type: 'number' },
                    ],
                }),
            ],
        },
        {
            // v3 → v4: Add GST reference tables (gst_rate_slabs, indian_states) + keywords on hsn_master
            toVersion: 4,
            steps: [
                // Create gst_rate_slabs reference table
                createTable({
                    name: 'gst_rate_slabs',
                    columns: [
                        { name: 'rate', type: 'number' },
                        { name: 'label', type: 'string' },
                        { name: 'is_active', type: 'boolean' },
                        { name: 'effective_from', type: 'number' },
                        { name: 'sort_order', type: 'number' },
                        { name: 'version', type: 'number' },
                        { name: 'is_synced', type: 'boolean' },
                        { name: 'is_deleted', type: 'boolean' },
                        { name: 'created_at', type: 'number' },
                        { name: 'updated_at', type: 'number' },
                    ],
                }),

                // Create indian_states reference table
                createTable({
                    name: 'indian_states',
                    columns: [
                        { name: 'code', type: 'string', isIndexed: true },
                        { name: 'name', type: 'string' },
                        { name: 'short_name', type: 'string' },
                        { name: 'is_ut', type: 'boolean' },
                        { name: 'is_active', type: 'boolean' },
                        { name: 'version', type: 'number' },
                        { name: 'is_synced', type: 'boolean' },
                        { name: 'is_deleted', type: 'boolean' },
                        { name: 'created_at', type: 'number' },
                        { name: 'updated_at', type: 'number' },
                    ],
                }),

                // Add keywords column to hsn_master for full-text search
                addColumns({
                    table: 'hsn_master',
                    columns: [
                        { name: 'keywords', type: 'string', isOptional: true },
                    ],
                }),
            ],
        },
        {
            // v4 → v5: RBAC & Onboarding — new columns on merchants/stores/users + user_invitations table
            toVersion: 5,
            steps: [
                // Merchant: onboarding status, approval tracking, subscription limits
                addColumns({
                    table: 'merchants',
                    columns: [
                        { name: 'onboarding_status', type: 'string' },
                        { name: 'approved_by_id', type: 'string', isOptional: true },
                        { name: 'approved_at', type: 'number', isOptional: true },
                        { name: 'subscription_plan', type: 'string' },
                        { name: 'max_stores', type: 'number' },
                        { name: 'max_users', type: 'number' },
                    ],
                }),

                // Store: operational status (ACTIVE / FLAGGED / SUSPENDED)
                addColumns({
                    table: 'stores',
                    columns: [
                        { name: 'status', type: 'string' },
                    ],
                }),

                // User: onboarding status, approval tracking, invitation link, login timestamp
                addColumns({
                    table: 'users',
                    columns: [
                        { name: 'onboarding_status', type: 'string' },
                        { name: 'approved_by_id', type: 'string', isOptional: true },
                        { name: 'approved_at', type: 'number', isOptional: true },
                        { name: 'invited_by_id', type: 'string', isOptional: true },
                        { name: 'last_login_at', type: 'number', isOptional: true },
                    ],
                }),

                // New table: staff invitation workflow
                createTable({
                    name: 'user_invitations',
                    columns: [
                        { name: 'merchant_id', type: 'string', isIndexed: true },
                        { name: 'store_id', type: 'string', isIndexed: true },
                        { name: 'phone', type: 'string', isIndexed: true },
                        { name: 'name', type: 'string', isOptional: true },
                        { name: 'role', type: 'string' },
                        { name: 'permissions', type: 'string', isOptional: true },
                        { name: 'status', type: 'string', isIndexed: true },
                        { name: 'invited_by_id', type: 'string' },
                        { name: 'expires_at', type: 'number', isOptional: true },
                        { name: 'requires_approval', type: 'boolean' },
                        { name: 'version', type: 'number' },
                        { name: 'is_synced', type: 'boolean' },
                        { name: 'is_deleted', type: 'boolean' },
                        { name: 'created_at', type: 'number' },
                        { name: 'updated_at', type: 'number' },
                    ],
                }),
            ],
        },
        {
            // v5 → v6: Oversell prevention — add last_sync_quantity to batches
            toVersion: 6,
            steps: [
                addColumns({
                    table: 'batches',
                    columns: [
                        { name: 'last_sync_quantity', type: 'number', isOptional: true },
                    ],
                }),
            ],
        },
        {
            // v6 → v7: Sync parity — rejection_reason on merchants, source on stock_movements, admin_user_id on audit_logs
            toVersion: 7,
            steps: [
                // Merchant: admin rejection reason
                addColumns({
                    table: 'merchants',
                    columns: [
                        { name: 'rejection_reason', type: 'string', isOptional: true },
                    ],
                }),

                // StockMovement: track origin (MOBILE/WEB/IMPORT/ADJUSTMENT)
                addColumns({
                    table: 'stock_movements',
                    columns: [
                        { name: 'source', type: 'string', isOptional: true },
                    ],
                }),

                // AuditLog: admin portal action attribution
                addColumns({
                    table: 'audit_logs',
                    columns: [
                        { name: 'admin_user_id', type: 'string', isOptional: true },
                    ],
                }),
            ],
        },
        {
            // v7 → v8: Add store_id to attendance for multi-store isolation + unique constraint fix
            toVersion: 8,
            steps: [
                addColumns({
                    table: 'attendance',
                    columns: [
                        { name: 'store_id', type: 'string' },
                    ],
                }),
            ],
        },
        {
            // v8 → v9: Add missing updated_at to 10 domain tables for CRDT sync parity
            toVersion: 9,
            steps: [
                addColumns({
                    table: 'stock_transfer_items',
                    columns: [{ name: 'updated_at', type: 'number' }],
                }),
                addColumns({
                    table: 'stock_movements',
                    columns: [{ name: 'updated_at', type: 'number' }],
                }),
                addColumns({
                    table: 'po_line_items',
                    columns: [{ name: 'updated_at', type: 'number' }],
                }),
                addColumns({
                    table: 'purchase_return_items',
                    columns: [{ name: 'updated_at', type: 'number' }],
                }),
                addColumns({
                    table: 'line_items',
                    columns: [{ name: 'updated_at', type: 'number' }],
                }),
                addColumns({
                    table: 'payments',
                    columns: [{ name: 'updated_at', type: 'number' }],
                }),
                addColumns({
                    table: 'loyalty_transactions',
                    columns: [{ name: 'updated_at', type: 'number' }],
                }),
                addColumns({
                    table: 'attendance',
                    columns: [{ name: 'updated_at', type: 'number' }],
                }),
                addColumns({
                    table: 'commissions',
                    columns: [{ name: 'updated_at', type: 'number' }],
                }),
                addColumns({
                    table: 'expense_categories',
                    columns: [{ name: 'updated_at', type: 'number' }],
                }),
            ],
        },
        {
            // v9 → v10: Add isDeleted to e_invoices and e_way_bills for soft-delete sync parity
            toVersion: 10,
            steps: [
                addColumns({
                    table: 'e_invoices',
                    columns: [{ name: 'is_deleted', type: 'boolean' }],
                }),
                addColumns({
                    table: 'e_way_bills',
                    columns: [{ name: 'is_deleted', type: 'boolean' }],
                }),
            ],
        },
        {
            // v10 → v11: Device-specific invoice numbering to prevent collisions
            toVersion: 11,
            steps: [
                addColumns({
                    table: 'invoices',
                    columns: [{ name: 'device_id', type: 'string', isOptional: true }],
                }),
                addColumns({
                    table: 'devices',
                    columns: [
                        { name: 'store_id', type: 'string', isOptional: true },
                        { name: 'device_slot', type: 'number', isOptional: true },
                    ],
                }),
            ],
        },
        {
            // v11 → v12: Product currentStock delta-rebase (collision-proof stock sync)
            toVersion: 12,
            steps: [
                addColumns({
                    table: 'products',
                    columns: [{ name: 'last_sync_current_stock', type: 'number', isOptional: true }],
                }),
            ],
        },
        {
            // v12 → v13: Customer/Supplier balance delta-rebase
            toVersion: 13,
            steps: [
                addColumns({
                    table: 'customers',
                    columns: [
                        { name: 'last_sync_balance', type: 'number', isOptional: true },
                        { name: 'last_sync_total_spend', type: 'number', isOptional: true },
                        { name: 'last_sync_loyalty_points', type: 'number', isOptional: true },
                        { name: 'last_sync_visit_count', type: 'number', isOptional: true },
                    ],
                }),
                addColumns({
                    table: 'suppliers',
                    columns: [
                        { name: 'last_sync_current_balance', type: 'number', isOptional: true },
                    ],
                }),
            ],
        },
        {
            // v13 → v14: Promotions usedCount delta-rebase
            toVersion: 14,
            steps: [
                addColumns({
                    table: 'promotions',
                    columns: [
                        { name: 'last_sync_used_count', type: 'number', isOptional: true },
                    ],
                }),
            ],
        },
        {
            // v14 → v15: Push Idempotency Tracking
            toVersion: 15,
            steps: [
                addColumns({
                    table: 'sync_logs',
                    columns: [
                        { name: 'push_id', type: 'string', isOptional: true },
                    ],
                }),
            ],
        },
        {
            // v15 → v16: E-Invoicing Support
            toVersion: 16,
            steps: [
                createTable({
                    name: 'e_invoices',
                    columns: [
                        { name: 'invoice_id', type: 'string', isIndexed: true },
                        { name: 'irn', type: 'string', isOptional: true },
                        { name: 'ack_number', type: 'string', isOptional: true },
                        { name: 'ack_date', type: 'number', isOptional: true },
                        { name: 'signed_qr', type: 'string', isOptional: true },
                        { name: 'signed_invoice', type: 'string', isOptional: true },
                        { name: 'status', type: 'string' },
                        { name: 'error_message', type: 'string', isOptional: true },
                        { name: 'version', type: 'number' },
                        { name: 'is_synced', type: 'boolean' },
                        { name: 'is_deleted', type: 'boolean' },
                        { name: 'created_at', type: 'number' },
                        { name: 'updated_at', type: 'number' },
                    ]
                })
            ]
        },
        {
            // v17 → v18: HSN Cache + Reorder Suggestions (AI-powered tables)
            toVersion: 18,
            steps: [
                createTable({
                    name: 'hsn_cache',
                    columns: [
                        { name: 'product_query', type: 'string', isIndexed: true },
                        { name: 'hsn_code', type: 'string' },
                        { name: 'description', type: 'string' },
                        { name: 'gst_rate', type: 'number' },
                        { name: 'confidence', type: 'string' },
                        { name: 'alternatives_json', type: 'string' },
                        { name: 'note', type: 'string', isOptional: true },
                        { name: 'source', type: 'string' },
                        { name: 'hit_count', type: 'number' },
                        { name: 'created_at', type: 'number' },
                        { name: 'updated_at', type: 'number' },
                    ]
                }),
                createTable({
                    name: 'reorder_suggestions',
                    columns: [
                        { name: 'store_id', type: 'string', isIndexed: true },
                        { name: 'merchant_id', type: 'string', isIndexed: true },
                        { name: 'generated_at', type: 'number' },
                        { name: 'urgent_json', type: 'string' },
                        { name: 'plan_json', type: 'string' },
                        { name: 'reduce_json', type: 'string' },
                        { name: 'seasonal_tip', type: 'string', isOptional: true },
                        { name: 'is_read', type: 'boolean' },
                    ]
                }),
            ]
        },
        {
            // v16 → v17: Supplier Import Mappings (read-only on mobile, configured via web-admin)
            toVersion: 17,
            steps: [
                createTable({
                    name: 'supplier_import_mappings',
                    columns: [
                        { name: 'merchant_id', type: 'string', isIndexed: true },
                        { name: 'supplier_id', type: 'string', isIndexed: true },
                        { name: 'mapping_name', type: 'string' },
                        { name: 'column_mappings', type: 'string' },
                        { name: 'file_type', type: 'string' },
                        { name: 'header_row', type: 'number' },
                        { name: 'skip_rows', type: 'number' },
                        { name: 'notes', type: 'string', isOptional: true },
                        { name: 'is_default', type: 'boolean' },
                        { name: 'version', type: 'number' },
                        { name: 'is_synced', type: 'boolean' },
                        { name: 'is_deleted', type: 'boolean' },
                        { name: 'created_at', type: 'number' },
                        { name: 'updated_at', type: 'number' },
                    ]
                }),
            ]
        },
        {
            // v18 → v19: Sync parity (updated_at on 3 tables) + UPI ID on stores
            toVersion: 19,
            steps: [
                addColumns({
                    table: 'hsn_master',
                    columns: [
                        { name: 'updated_at', type: 'number' },
                    ]
                }),
                addColumns({
                    table: 'notifications',
                    columns: [
                        { name: 'updated_at', type: 'number' },
                    ]
                }),
                addColumns({
                    table: 'reorder_suggestions',
                    columns: [
                        { name: 'updated_at', type: 'number' },
                    ]
                }),
                addColumns({
                    table: 'stores',
                    columns: [
                        { name: 'upi_id', type: 'string', isOptional: true },
                    ]
                }),
            ]
        },
        {
            // v19 → v20: Schema parity — add created_at to hsn_master
            toVersion: 20,
            steps: [
                addColumns({
                    table: 'hsn_master',
                    columns: [
                        { name: 'created_at', type: 'number' },
                    ]
                }),
            ]
        },
        {
            // v20 → v21: Subscription identity & billing session architecture
            toVersion: 21,
            steps: [
                // Merchant: subscription & device management fields
                addColumns({
                    table: 'merchants',
                    columns: [
                        { name: 'owner_phone', type: 'string', isOptional: true },
                        { name: 'plan_status', type: 'string' },
                        { name: 'plan_expires_at', type: 'number', isOptional: true },
                        { name: 'max_devices', type: 'number' },
                    ]
                }),
                // User: primary owner flag
                addColumns({
                    table: 'users',
                    columns: [
                        { name: 'is_primary_owner', type: 'boolean' },
                    ]
                }),
                // Store: billing session configuration
                addColumns({
                    table: 'stores',
                    columns: [
                        { name: 'require_day_open', type: 'boolean' },
                        { name: 'emergency_bill_roles', type: 'string', isOptional: true },
                    ]
                }),
                // Invoice: billing session link + emergency flag
                addColumns({
                    table: 'invoices',
                    columns: [
                        { name: 'billing_session_id', type: 'string', isOptional: true },
                        { name: 'is_emergency', type: 'boolean' },
                    ]
                }),
                // Device: shared counter support
                addColumns({
                    table: 'devices',
                    columns: [
                        { name: 'pin_hash', type: 'string', isOptional: true },
                        { name: 'device_type', type: 'string' },
                    ]
                }),
                // Notification: CRDT sync + scoping fields
                addColumns({
                    table: 'notifications',
                    columns: [
                        { name: 'store_id', type: 'string', isOptional: true, isIndexed: true },
                        { name: 'user_id', type: 'string', isOptional: true, isIndexed: true },
                        { name: 'version', type: 'number' },
                        { name: 'is_synced', type: 'boolean' },
                        { name: 'is_deleted', type: 'boolean' },
                    ]
                }),
                // New: BillingSession table
                createTable({
                    name: 'billing_sessions',
                    columns: [
                        { name: 'store_id', type: 'string', isIndexed: true },
                        { name: 'device_id', type: 'string', isIndexed: true },
                        { name: 'user_id', type: 'string' },
                        { name: 'session_date', type: 'number', isIndexed: true },
                        { name: 'opened_at', type: 'number' },
                        { name: 'closed_at', type: 'number', isOptional: true },
                        { name: 'closed_by_id', type: 'string', isOptional: true },
                        { name: 'opening_cash', type: 'number' },
                        { name: 'closing_cash', type: 'number', isOptional: true },
                        { name: 'expected_cash', type: 'number', isOptional: true },
                        { name: 'variance', type: 'number', isOptional: true },
                        { name: 'status', type: 'string', isIndexed: true },
                        { name: 'version', type: 'number' },
                        { name: 'is_synced', type: 'boolean' },
                        { name: 'is_deleted', type: 'boolean' },
                        { name: 'created_at', type: 'number' },
                        { name: 'updated_at', type: 'number' },
                    ]
                }),
            ]
        },
        {
            // v21 → v22: event-sourced batch stock — link each movement to its batch
            // so batch.quantity can be derived from the immutable movement log.
            toVersion: 22,
            steps: [
                addColumns({
                    table: 'stock_movements',
                    columns: [
                        { name: 'batch_id', type: 'string', isOptional: true, isIndexed: true },
                    ]
                }),
            ]
        },
        {
            // v22 → v23: index payments.received_at. The Sales report selects
            // payments on a cash basis (when the money arrived) rather than by
            // invoice_id, which would otherwise full-scan the payments table on
            // every report load. Index only — no column is added, so nothing new
            // is pushed to the sync API.
            toVersion: 23,
            steps: [
                unsafeExecuteSql(
                    'CREATE INDEX IF NOT EXISTS payments_received_at ON payments (received_at);'
                ),
            ]
        },
        {
            // v23 → v24: snapshot the unit cost onto each sold line.
            //
            // REQUIRES backend migration 20260710010000_add_cost_price_to_line_items
            // to be DEPLOYED FIRST. WatermelonDB pushes every schema column and the
            // server's sanitizeForPrisma is a denylist, so a client sending
            // `costPrice` to a database without the column fails the whole push.
            toVersion: 24,
            steps: [
                addColumns({
                    table: 'line_items',
                    columns: [
                        { name: 'cost_price', type: 'number', isOptional: true },
                    ]
                }),
            ]
        },
        {
            // v24 → v25: stamp the drawer onto payments and expenses.
            //
            // REQUIRES backend migration
            // 20260914000000_add_billing_session_to_payments_expenses to be
            // DEPLOYED FIRST. WatermelonDB pushes every schema column and the
            // server's sanitizeForPrisma is a denylist, so a client sending
            // `billingSessionId` to a database without the column fails the
            // whole push.
            //
            // Day close reached the session through the payment's INVOICE,
            // which cannot see a khata payment collected today against last
            // week's invoice, and cannot tell two counters apart. Expenses were
            // worse: the whole store's spending for the calendar day was
            // subtracted from every open session.
            //
            // Nullable, and rows written before this stay null: a session that
            // was open at the time of the upgrade keeps reporting on the
            // invoice-linked basis for its own payments, which is what it did
            // before. Nothing is back-filled — the drawer a historical payment
            // crossed is not recoverable, and guessing it would put invented
            // numbers into a cash count.
            toVersion: 25,
            steps: [
                addColumns({
                    table: 'payments',
                    columns: [
                        { name: 'billing_session_id', type: 'string', isOptional: true, isIndexed: true },
                    ]
                }),
                addColumns({
                    table: 'expenses',
                    columns: [
                        { name: 'billing_session_id', type: 'string', isOptional: true, isIndexed: true },
                    ]
                }),
            ]
        },
    ],
})
